import React, { useEffect, useState, useRef } from 'react';
import { audioService, Audio } from '../services/audioService';
import { useAuth } from '../context/AuthContext';
import { toMediaUrl } from '../config/api';
import { PaginationControls } from '../components/PaginationControls';

export const Mediatheque: React.FC = () => {
  const { user } = useAuth();
  const [audios, setAudios] = useState<Audio[]>([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    categorie: '',
    personne: '',
    search: '',
  });
  const [currentAudio, setCurrentAudio] = useState<Audio | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const audioRef = useRef<HTMLAudioElement>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  useEffect(() => {
    setCurrentPage(1);
    fetchAudios(1);
  }, [filters.categorie, filters.personne, filters.search]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const updateTime = () => setCurrentTime(audio.currentTime);
    const updateDuration = () => setDuration(audio.duration);
    const handleEnded = () => {
      setIsPlaying(false);
      setCurrentTime(0);
    };

    audio.addEventListener('timeupdate', updateTime);
    audio.addEventListener('loadedmetadata', updateDuration);
    audio.addEventListener('ended', handleEnded);

    return () => {
      audio.removeEventListener('timeupdate', updateTime);
      audio.removeEventListener('loadedmetadata', updateDuration);
      audio.removeEventListener('ended', handleEnded);
    };
  }, [currentAudio]);

  const fetchAudios = async (page: number = 1) => {
    setLoading(true);
    try {
      const data = await audioService.getAll(page, 6, filters);
      setAudios(data.results || []);
      setTotalCount(data.count || 0);
      setTotalPages(Math.ceil((data.count || 0) / 6));
    } catch (error) {
      console.error('Erreur:', error);
      setAudios([]);
    } finally {
      setLoading(false);
    }
  };

  const handlePlay = async (audio: Audio) => {
    if (currentAudio?.id === audio.id) {
      // Toggle play/pause
      if (isPlaying) {
        audioRef.current?.pause();
        setIsPlaying(false);
      } else {
        audioRef.current?.play().catch((error) => {
          console.error('Erreur de lecture audio:', error);
          setIsPlaying(false);
        });
        setIsPlaying(true);
      }
    } else {
      // Nouvel audio
      setCurrentAudio(audio);
      setIsPlaying(true);
      if (user) {
        try {
          await audioService.ecouter(audio.id);
        } catch (error) {
          console.error('Erreur:', error);
        }
      }
      setTimeout(() => {
        if (audioRef.current) {
          audioRef.current.play().catch((error) => {
            console.error('Erreur de lecture audio:', error);
          });
        }
      }, 100);
    }
  };

  const handleLike = async (id: number) => {
    if (!user) return;
    try {
      await audioService.like(id);
      fetchAudios();
      if (currentAudio?.id === id) {
        const updated = await audioService.getById(id);
        setCurrentAudio(updated);
      }
    } catch (error) {
      console.error('Erreur:', error);
    }
  };

  const handleDownload = (audio: Audio) => {
    window.open(audio.audio_file, '_blank');
    if (user) {
      audioService.telecharger(audio.id).catch(console.error);
    }
  };

  const formatTime = (seconds: number) => {
    if (!seconds || isNaN(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getCategorieInfo = (cat: string) => {
    const categories: { [key: string]: { text: string; icon: string; color: string } } = {
      bourde: { text: 'Bourde', icon: '📖', color: 'from-green-500 to-green-600' },
      khilass: { text: 'Khilass', icon: '✨', color: 'from-green-500 to-green-600' },
      gamou: { text: 'Gamou', icon: '🎉', color: 'from-green-500 to-green-600' },
      dahira: { text: 'Dahira', icon: '🤝', color: 'from-green-500 to-green-600' },
      coran: { text: 'Coran', icon: '📿', color: 'from-green-500 to-green-600' },
    };
    return categories[cat] || { text: cat, icon: '🎵', color: 'from-gray-500 to-gray-600' };
  };

  return (
    <div className="min-h-screen pb-32">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-white via-green-50/30 to-green-50/50 py-20 overflow-hidden">
        {/* Motifs décoratifs */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-20 -right-20 w-96 h-96 bg-green-200/20 rounded-full blur-3xl"></div>
          <div className="absolute -bottom-20 -left-20 w-96 h-96 bg-green-300/20 rounded-full blur-3xl"></div>
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-12">
            <div className="relative inline-block bg-gradient-to-br from-green-600 via-green-700 to-green-800 rounded-3xl px-8 py-8 shadow-2xl overflow-hidden group mb-8">
              {/* Effet de brillance animé */}
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent transform -skew-x-12 -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
              
              {/* Contenu */}
              <div className="relative z-10">
                <h1 className="text-5xl sm:text-6xl font-bold text-white mb-4 drop-shadow-lg">
                  Médiathèque
                </h1>
                <p className="text-green-50 text-xl font-medium">
                  Écoutez et téléchargez nos audios religieux
                </p>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* Liste des Audios */}
      <section className="relative bg-gradient-to-br from-green-50/30 via-white to-green-50/50 py-6 sm:py-8 md:py-10 overflow-hidden">
        {/* Motifs décoratifs */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-0 right-1/4 w-72 h-72 bg-green-200/15 rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 left-1/4 w-80 h-80 bg-green-100/20 rounded-full blur-3xl"></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          {/* Filtres mobiles - visible seulement sur mobile/tablet */}
          <div className="lg:hidden mb-4">
            <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-4 shadow-lg border border-green-100/50">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <select
                  value={filters.categorie}
                  onChange={(e) => setFilters({ ...filters, categorie: e.target.value })}
                  className="px-3 py-2 border-2 border-green-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all bg-white text-sm"
                >
                  <option value="">Toutes catégories</option>
                  <option value="bourde">📖 Bourde</option>
                  <option value="khilass">✨ Khilass</option>
                  <option value="gamou">🎉 Gamou</option>
                  <option value="dahira">🤝 Dahira</option>
                  <option value="coran">📿 Coran</option>
                </select>
                
                <input
                  type="text"
                  placeholder="🔍 Par personne..."
                  value={filters.personne}
                  onChange={(e) => setFilters({ ...filters, personne: e.target.value })}
                  className="px-3 py-2 border-2 border-green-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all bg-white text-sm"
                />
                
                <input
                  type="text"
                  placeholder="🔍 Par titre..."
                  value={filters.search}
                  onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                  className="px-3 py-2 border-2 border-green-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all bg-white text-sm"
                />
              </div>
            </div>
          </div>
          
          {/* Layout latéral avec filtres et contenu */}
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 lg:gap-6">
            {/* Sidebar filtres - seulement sur desktop */}
            <div className="hidden lg:block lg:col-span-1">
              <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-4 shadow-lg border border-green-100/50 sticky top-4">
                <h3 className="text-lg font-bold text-gray-900 mb-4">🔍 Filtres</h3>
                <div className="space-y-4">
                  <select
                    value={filters.categorie}
                    onChange={(e) => setFilters({ ...filters, categorie: e.target.value })}
                    className="w-full px-4 py-2 border-2 border-green-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all bg-white text-sm"
                  >
                    <option value="">Toutes catégories</option>
                    <option value="bourde">📖 Bourde</option>
                    <option value="khilass">✨ Khilass</option>
                    <option value="gamou">🎉 Gamou</option>
                    <option value="dahira">🤝 Dahira</option>
                    <option value="coran">📿 Coran</option>
                  </select>
                  
                  <input
                    type="text"
                    placeholder="🔍 Par personne..."
                    value={filters.personne}
                    onChange={(e) => setFilters({ ...filters, personne: e.target.value })}
                    className="w-full px-4 py-2 border-2 border-green-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all bg-white text-sm"
                  />
                  
                  <input
                    type="text"
                    placeholder="🔍 Par titre..."
                    value={filters.search}
                    onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                    className="w-full px-4 py-2 border-2 border-green-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all bg-white text-sm"
                  />
                </div>
              </div>
            </div>
            
            {/* Contenu principal */}
            <div className="lg:col-span-3">
          {loading ? (
            <div className="flex justify-center items-center py-32">
              <div className="relative">
                <div className="animate-spin rounded-full h-20 w-20 border-4 border-green-200 border-t-green-700"></div>
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="text-2xl">🎵</span>
                </div>
              </div>
            </div>
          ) : audios.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {audios.map((audio, index) => {
                const catInfo = getCategorieInfo(audio.categorie);
                return (
                  <div
                    key={audio.id}
                    className="group animate-slide-up bg-white rounded-3xl shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-3 overflow-hidden border border-green-100/50"
                    style={{ animationDelay: `${index * 0.1}s` }}
                  >
                    {/* Header: afficher la photo si dispo, sinon gradient */}
                    {audio.photo ? (
                      <div className="relative h-48 overflow-hidden bg-gray-100">
                        <img
                          src={toMediaUrl(audio.photo)}
                          alt={audio.titre}
                          className="w-full h-full object-cover object-center"
                        />
                        <div className="absolute top-4 left-4">
                          <span className="px-4 py-2 bg-black/40 backdrop-blur-sm rounded-full text-sm font-bold text-white">
                            {catInfo.icon} {catInfo.text}
                          </span>
                        </div>
                      </div>
                    ) : (
                      <div className={`relative h-48 bg-gradient-to-br ${catInfo.color} overflow-hidden`}>
                        <div className="absolute inset-0 flex items-center justify-center">
                          <span className="text-7xl opacity-50">{catInfo.icon}</span>
                        </div>
                        <div className="absolute top-4 left-4">
                          <span className="px-4 py-2 bg-white/20 backdrop-blur-sm rounded-full text-sm font-bold text-white">
                            {catInfo.icon} {catInfo.text}
                          </span>
                        </div>
                      </div>
                    )}

                    {/* Contenu */}
                    <div className="p-6">
                      <h3 className="text-2xl font-bold text-gray-900 mb-3 group-hover:text-green-700 transition-colors line-clamp-2">
                        {audio.titre}
                      </h3>
                      
                      <div className="space-y-2 mb-4">
                        <p className="text-gray-700 font-medium text-lg">
                          🎤 {audio.personne}
                        </p>
                        {audio.duree_formatee && (
                          <p className="text-gray-600 text-sm">⏱️ {audio.duree_formatee}</p>
                        )}
                      </div>

                      {/* Stats */}
                      <div className="flex items-center space-x-4 text-sm text-gray-500 mb-6 pb-6 border-b border-gray-100">
                        <span className="flex items-center space-x-1">
                          <span>👁️</span>
                          <span>{audio.nombre_ecoutes || 0}</span>
                        </span>
                        <span className="flex items-center space-x-1">
                          <span>❤️</span>
                          <span>{audio.nombre_likes || 0}</span>
                        </span>
                        <span className="flex items-center space-x-1">
                          <span>📥</span>
                          <span>{audio.nombre_downloads || 0}</span>
                        </span>
                      </div>

                      {/* Actions */}
                      <div className="flex space-x-3">
                        <button
                          onClick={() => handlePlay(audio)}
                          className={`flex-1 px-4 py-3 rounded-xl font-semibold transition-all duration-300 transform hover:scale-105 ${
                            currentAudio?.id === audio.id && isPlaying
                              ? 'bg-red-500 hover:bg-red-600 text-white'
                              : 'bg-green-600 hover:bg-green-700 text-white'
                          }`}
                        >
                          {currentAudio?.id === audio.id && isPlaying ? '⏸️ Pause' : '▶️ Écouter'}
                        </button>
                        {user && (
                          <button
                            onClick={() => handleLike(audio.id)}
                            className={`px-4 py-3 rounded-xl font-semibold transition-all duration-300 transform hover:scale-105 ${
                              audio.is_liked
                                ? 'bg-red-500 hover:bg-red-600 text-white'
                                : 'bg-gray-200 hover:bg-gray-300 text-gray-700'
                            }`}
                          >
                            {audio.is_liked ? '❤️' : '🤍'}
                          </button>
                        )}
                        <button
                          onClick={() => handleDownload(audio)}
                          className="px-4 py-3 bg-green-600 hover:bg-green-700 text-white rounded-xl font-semibold transition-all duration-300 transform hover:scale-105"
                        >
                          📥
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-32">
              <div className="inline-block bg-white/90 backdrop-blur-sm rounded-3xl p-12 shadow-xl border border-green-100/50">
                <div className="text-8xl mb-6">🎵</div>
                <h3 className="text-3xl font-bold text-gray-900 mb-4">Aucun audio trouvé</h3>
                <p className="text-gray-600 text-lg">
                  Essayez de modifier vos filtres pour voir plus de résultats
                </p>
              </div>
            </div>
          )}
          
          {/* Pagination */}
          {totalPages > 1 && (
            <PaginationControls
              currentPage={currentPage}
              totalPages={totalPages}
              loading={loading}
              onPageChange={(page) => {
                setCurrentPage(page);
                fetchAudios(page);
              }}
            />
          )}
            </div>
          </div>
        </div>
      </section>

      {/* Player Audio Fixe */}
      {currentAudio && (
        <div className="fixed bottom-0 left-0 right-0 bg-white/95 backdrop-blur-lg shadow-2xl border-t border-gray-200 z-50 animate-slide-up">
          <audio
            ref={audioRef}
            src={currentAudio.audio_file}
            onPlay={() => setIsPlaying(true)}
            onPause={() => setIsPlaying(false)}
            className="hidden"
          />
          
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
            <div className="flex items-center space-x-6">
              {/* Info audio */}
              <div className="flex items-center space-x-4 flex-1 min-w-0">
                <div className={`w-16 h-16 rounded-xl bg-gradient-to-br ${getCategorieInfo(currentAudio.categorie).color} flex items-center justify-center flex-shrink-0 shadow-lg`}>
                  <span className="text-3xl">{getCategorieInfo(currentAudio.categorie).icon}</span>
                </div>
                <div className="min-w-0 flex-1">
                  <h4 className="font-bold text-gray-900 text-lg line-clamp-1">{currentAudio.titre}</h4>
                  <p className="text-gray-600 text-sm line-clamp-1">{currentAudio.personne} - {getCategorieInfo(currentAudio.categorie).text}</p>
                </div>
              </div>

              {/* Controls */}
              <div className="flex items-center space-x-4 flex-1 max-w-md">
                <button
                  onClick={() => {
                    if (isPlaying) {
                      try {
                        audioRef.current?.pause();
                      } catch (e) {}
                      setIsPlaying(false);
                    } else {
                      audioRef.current?.play().catch((error) => {
                        console.error('Erreur de lecture audio:', error);
                        setIsPlaying(false);
                      });
                      setIsPlaying(true);
                    }
                  }}
                  className="w-12 h-12 bg-pink-600 hover:bg-pink-700 text-white rounded-full flex items-center justify-center transition-all duration-300 transform hover:scale-110 shadow-lg"
                >
                  {isPlaying ? '⏸️' : '▶️'}
                </button>
                
                <div className="flex-1">
                  <div className="flex items-center justify-between text-xs text-gray-600 mb-1">
                    <span>{formatTime(currentTime)}</span>
                    <span>{formatTime(duration)}</span>
                  </div>
                  <div 
                    className="h-2 bg-gray-200 rounded-full overflow-hidden cursor-pointer relative"
                    onClick={(e) => {
                      if (!audioRef.current || !duration) return;
                      const rect = e.currentTarget.getBoundingClientRect();
                      const percent = (e.clientX - rect.left) / rect.width;
                      const newTime = percent * duration;
                      audioRef.current.currentTime = newTime;
                      setCurrentTime(newTime);
                    }}
                  >
                    <div
                      className="h-full bg-gradient-to-r from-pink-500 to-pink-600 transition-all duration-300"
                      style={{ width: duration ? `${(currentTime / duration) * 100}%` : '0%' }}
                    ></div>
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="flex items-center space-x-3">
                {user && (
                  <button
                    onClick={() => handleLike(currentAudio.id)}
                    className={`w-10 h-10 rounded-full flex items-center justify-center transition-all duration-300 ${
                      currentAudio.is_liked
                        ? 'bg-red-500 hover:bg-red-600 text-white'
                        : 'bg-gray-200 hover:bg-gray-300 text-gray-700'
                    }`}
                  >
                    {currentAudio.is_liked ? '❤️' : '🤍'}
                  </button>
                )}
                <button
                  onClick={() => handleDownload(currentAudio)}
                  className="w-10 h-10 bg-green-600 hover:bg-green-700 text-white rounded-full flex items-center justify-center transition-all duration-300"
                >
                  📥
                </button>
                <button
                  onClick={() => {
                    try {
                      audioRef.current?.pause();
                    } catch (e) {}
                    setCurrentAudio(null);
                    setIsPlaying(false);
                    setCurrentTime(0);
                    setDuration(0);
                  }}
                  className="w-10 h-10 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-full flex items-center justify-center transition-all duration-300"
                >
                  ✖️
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
